import React, { useCallback, useRef } from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import { Button, ButtonGroup, rem } from 'design-system';
import Modal from 'utils/packages/Modal';
import Text from 'design-system/Text';
import { useAppDispatch, useAppStore } from 'store/hooks';
import Links from 'utils/links';
import { getStripePrices } from '../actions/stripe';
import { checkGooglePlayServices } from '../iap';
import { selectShouldShowStripe } from '../selectors';
import useLoadIapSubscriptions from './useLoadIapSubscriptions';
const useIAPSubscriptionsNotFoundError = () => {
    const loadIapSubscriptions = useLoadIapSubscriptions();
    const store = useAppStore();
    const dispatch = useAppDispatch();
    const hasRetried = useRef(false);
    const onError = useCallback(async ({ retry }) => {
        const canUseStripe = selectShouldShowStripe(store.getState());
        if (canUseStripe && !hasRetried.current) {
            const response = await dispatch(getStripePrices());
            if (response?.payload) {
                // hasRetried is to prevent calling the API infinitely in the case a payload was returned but it had no subscriptions
                hasRetried.current = true;
                retry();
                return;
            }
        }
        if (!canUseStripe && Platform.OS === 'android') {
            try {
                await checkGooglePlayServices();
            }
            catch {
                return;
            }
        }
        const onPressSupport = () => {
            Modal.hide(() => {
                Links.openKnownLink('noSubscriptions');
            });
        };
        const onPressRetry = () => {
            Modal.hide(async () => {
                await loadIapSubscriptions();
                hasRetried.current = false;
                retry();
            });
        };
        Modal.show(<View>
          <Text TextThin-14 Secondary centered style={styles.text}>
            We have failed to load our premium plans. You might need to check if
            you have in-app purchases disabled in your{' '}
            {Platform.select({
                android: 'Google Play',
                ios: 'App Store',
            })}{' '}
            account. This can also be related to the payment method you have
            added in your{' '}
            {Platform.select({
                android: 'Google',
                ios: 'Apple',
            })}{' '}
            account, which may no longer be valid. Please check both to access
            our premium plans.
          </Text>
          <ButtonGroup margins={false}>
            <Button title="Help" onPress={onPressSupport} gray/>
            <Button title="Try again" onPress={onPressRetry} brand/>
          </ButtonGroup>
        </View>, { title: 'Failed to load plans' });
    }, [loadIapSubscriptions]);
    return onError;
};
const styles = StyleSheet.create({
    text: {
        marginVertical: rem(8),
    },
});
export default useIAPSubscriptionsNotFoundError;
